/**
 * Odds conversion utilities for betting calculators.
 * American: +110, -150. Decimal: 2.1, 1.667. Fractional: 11/10, 2/3. Implied: 0.4762 (47.62%).
 */

/**
 * American odds to decimal odds.
 * @param {number} american - American odds (e.g. 110, -150)
 * @returns {number} Decimal odds
 */
export function americanToDecimal(american) {
  if (american === 0 || american === undefined || american === null) return 1;
  const a = Number(american);
  if (a >= 0) return 1 + a / 100;
  return 1 + 100 / Math.abs(a);
}

/**
 * Decimal odds to American odds.
 * @param {number} decimal - Decimal odds (e.g. 2.1)
 * @returns {number|null} American odds or null if invalid
 */
export function decimalToAmerican(decimal) {
  const d = Number(decimal);
  if (d <= 1 || Number.isNaN(d)) return null;
  if (d >= 2) return Math.round((d - 1) * 100);
  return Math.round(-100 / (d - 1));
}

/**
 * American odds to implied probability (0-1).
 * @param {number} american - American odds
 * @returns {number} Implied probability
 */
export function americanToImplied(american) {
  if (american === undefined || american === null) return 0;
  const a = Number(american);
  if (a >= 0) return 100 / (a + 100);
  return Math.abs(a) / (Math.abs(a) + 100);
}

/**
 * Implied probability (0-1 or 0-100) to American odds.
 * @param {number} implied - Probability (e.g. 0.6 or 60)
 * @returns {number|null} American odds
 */
export function impliedToAmerican(implied) {
  const p = Number(implied);
  if (Number.isNaN(p) || p <= 0 || p >= 1) {
    const pct = p >= 1 && p <= 100 ? p / 100 : null;
    if (pct == null || pct <= 0 || pct >= 1) return null;
    return impliedPctToAmerican(pct);
  }
  return impliedPctToAmerican(p);
}

function impliedPctToAmerican(p) {
  if (p <= 0 || p >= 1) return null;
  if (p > 0.5) return Math.round(-p / (1 - p) * 100);
  return Math.round((1 - p) / p * 100); // p === 0.5 => +100
}

/**
 * Parse fractional string "a/b" or "a b/c" to decimal odds.
 * @param {string} str - e.g. "2/3", "11/10"
 * @returns {number|null} Decimal odds
 */
export function fractionalToDecimal(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim().replace(/\s+/g, ' ');
  const parts = trimmed.split('/');
  if (parts.length !== 2) return null;
  const num = Number(parts[0].trim());
  const den = Number(parts[1].trim());
  if (Number.isNaN(num) || Number.isNaN(den) || den === 0) return null;
  return num / den + 1;
}

/**
 * Decimal odds to fractional string (simplified for display).
 * @param {number} decimal - Decimal odds
 * @returns {string} e.g. "2/3", "11/10"
 */
export function decimalToFractional(decimal) {
  const d = Number(decimal);
  if (Number.isNaN(d) || d <= 1) return '0/1';
  const x = d - 1;
  const den = 1000;
  let num = Math.round(x * den);
  if (num <= 0) return '0/1';
  const g = gcd(num, den);
  const n = num / g;
  const de = den / g;
  return `${n}/${de}`;
}

function gcd(a, b) {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a;
}

/**
 * To Win (profit) for a single bet. American odds and stake.
 * @param {number} american - American odds
 * @param {number} stake - Bet amount
 * @returns {number} Profit
 */
export function toWin(american, stake) {
  const a = Number(american);
  const s = Number(stake);
  if (Number.isNaN(a) || Number.isNaN(s) || s <= 0) return 0;
  if (a >= 0) return (s * a) / 100;
  return (s * 100) / Math.abs(a);
}

/**
 * Total payout (stake + to win) for a single bet.
 * @param {number} american - American odds
 * @param {number} stake - Bet amount
 * @returns {number}
 */
export function payout(american, stake) {
  return Number(stake) + toWin(american, stake);
}

/**
 * Multiply decimal odds for a parlay and return combined decimal.
 * @param {number[]} americanLegs - American odds for each leg
 * @returns {number} Combined decimal odds
 */
export function parlayDecimal(americanLegs) {
  const decimals = americanLegs
    .map((a) => americanToDecimal(a))
    .filter((d) => d > 1 && Number.isFinite(d));
  if (decimals.length === 0) return 1;
  return decimals.reduce((acc, d) => acc * d, 1);
}

/**
 * Parlay To Win (profit) and combined American odds.
 * @param {number[]} americanLegs - American odds per leg
 * @param {number} stake - Bet amount
 * @returns {{ parlayAmerican: number, toWin: number, parlayDecimal: number }}
 */
export function parlayToWin(americanLegs, stake) {
  const parlayDec = parlayDecimal(americanLegs);
  const american = decimalToAmerican(parlayDec);
  const toWin = stake * (parlayDec - 1);
  return {
    parlayDecimal: parlayDec,
    parlayAmerican: american != null ? american : 0,
    toWin: Number.isFinite(toWin) ? toWin : 0,
  };
}
